/**
 * Lightweight event taxonomy for e-commerce analytics.
 * Fans out to whichever pixels are loaded on window. All gated by consent.
 */
import { getConsent } from "./consent";

type EventName =
  | "page_view"
  | "view_item"
  | "view_item_list"
  | "select_item"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase"
  | "generate_lead"
  | "sign_up"
  | "click_amazon_cta"
  | "email_signup"
  | "sms_signup"
  | "bulk_order_inquiry"
  | "quiz_completed";

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: (...args: any[]) => void;
    ttq?: { track: (event: string, params?: Record<string, any>) => void };
    pintrk?: (...args: any[]) => void;
    snaptr?: (...args: any[]) => void;
    uetq?: any[];
  }
}

export function track(event: EventName, params: Record<string, any> = {}) {
  if (typeof window === "undefined") return;
  const consent = getConsent();
  // page_view is allowed under analytics; everything else needs marketing consent or analytics consent.
  if (!consent.decided && event !== "page_view") return;

  const payload = { event, ...params };

  if (consent.analytics) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    window.gtag?.("event", event, params);
  }

  if (consent.marketing) {
    const fbqMap: Partial<Record<EventName, string>> = {
      view_item: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      purchase: "Purchase",
      generate_lead: "Lead",
      email_signup: "Lead",
      sign_up: "CompleteRegistration",
    };
    const fbEvent = fbqMap[event];
    if (fbEvent) window.fbq?.("track", fbEvent, params);
    window.ttq?.track(event, params);
    window.pintrk?.("track", event, params);
    window.snaptr?.("track", event, params);
    if (Array.isArray(window.uetq)) window.uetq.push("event", event, params);
  }
}

const UTM_KEY = "glg.utm.v1";
const FIRST_TOUCH_KEY = "glg.first_touch.v1";

export function captureUtm() {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const utm: Record<string, string> = {};
  for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"]) {
    const v = url.searchParams.get(k);
    if (v) utm[k] = v;
  }
  if (Object.keys(utm).length === 0) return;
  const payload = {
    ...utm,
    landing_page: url.pathname,
    referrer: document.referrer || "",
    captured_at: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(UTM_KEY, JSON.stringify(payload));
    if (!window.localStorage.getItem(FIRST_TOUCH_KEY)) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(payload));
    }
  } catch {
    /* noop */
  }
}

export function getStoredUtm(): Record<string, any> {
  if (typeof window === "undefined") return {};
  try {
    const last = window.localStorage.getItem(UTM_KEY);
    const first = window.localStorage.getItem(FIRST_TOUCH_KEY);
    return {
      last_touch: last ? JSON.parse(last) : null,
      first_touch: first ? JSON.parse(first) : null,
    };
  } catch {
    return {};
  }
}
